/**
 * CanDo Payments — Shared Pricing Engine
 * Single source of truth for all sell rates and calculation logic.
 * Aligned to Commercial Pricing Structure v2.0 (2026-02)
 *
 * Used by: SavingsCalculator, InPersonQuoteBuilder, OnlineQuoteBuilder,
 *          CashVsCardComparator, HiddenFeesChecker (benchmarks)
 */

// --- CARD BAND DEFINITIONS ---
export const CARD_BANDS = [
  { key: "debit", label: "Consumer Debit" },
  { key: "credit", label: "Consumer Credit" },
  { key: "business_debit", label: "Business Debit" },
  { key: "business_credit", label: "Business Credit" },
  { key: "international", label: "International" },
  { key: "amex", label: "AMEX" },
];

// --- SELL RATES (to merchant) ---
export const IN_PERSON_SELL_RATES = {
  debit: 0.004,            // 0.40%
  credit: 0.008,           // 0.80%
  business_debit: 0.0149,  // 1.49%
  business_credit: 0.022,  // 2.20%
  international: 0.029,    // 2.90%
  amex: 0.019,             // 1.90%
};

export const ONLINE_SELL_RATES = {
  debit: 0.010,            // 1.00%
  credit: 0.010,           // 1.00%
  business_debit: 0.0149,  // 1.49%
  business_credit: 0.022,  // 2.20%
  international: 0.029,    // 2.90%
  amex: 0.019,             // 1.90%
};

// --- TRANSACTION FEES ---
export const AUTH_FEE_SELL = 0.03;      // 3p per transaction
export const GATEWAY_FEE_SELL = 0.15;   // 15p per transaction (EPG)

// --- FIXED MONTHLY COSTS ---
export const FIXED_MONTHLY = {
  pci: 6.00,                     // PCI Secure
  terminalStandard: 19.95,       // PAX A920 Pro standard
  terminalIntegrated: 22.95,     // PAX A920 Pro integrated
  eposPerStation: 40.00,
  gatewayMonthly: 0,             // EPG (£0/mo)
};

// --- DEFAULT CARD MIXES (%) — must sum to 100 ---
export const DEFAULT_MIX_IN_PERSON = {
  debit: 70, credit: 20, business_debit: 5, business_credit: 3, international: 0, amex: 2,
};

export const DEFAULT_MIX_ONLINE = {
  debit: 40, credit: 40, business_debit: 5, business_credit: 5, international: 5, amex: 5,
};

// Savings calculator uses in-person defaults for basic mode (debit/credit only)
export const DEFAULT_MIX_SAVINGS_BASIC = { debit: 85, credit: 15 };

// --- BENCHMARKS (for Hidden Fees Checker) ---
export const BENCHMARKS = {
  pciReasonable: 6,         // Aligned to CanDo PCI Secure sell price
  terminalPerUnit: 20,      // Reasonable per-terminal monthly
  authFee: 0.03,            // 3p (matches CanDo default sell)
  serviceFee: 5,            // Monthly service/admin fee
  otherFees: 5,             // Other recurring charges
};

// --- BLENDED 3-BAND RATES (for simpler calculators like CashVsCard) ---
export const BLENDED_IN_PERSON = {
  debit: IN_PERSON_SELL_RATES.debit,
  credit: IN_PERSON_SELL_RATES.credit,
  business: IN_PERSON_SELL_RATES.business_credit, // conservative: use higher rate
  authFee: AUTH_FEE_SELL,
};

export const BLENDED_ONLINE = {
  debit: ONLINE_SELL_RATES.debit,
  credit: ONLINE_SELL_RATES.credit,
  business: ONLINE_SELL_RATES.business_credit,
  authFee: AUTH_FEE_SELL,
  gatewayFeePerTransaction: GATEWAY_FEE_SELL,
};

// --- CALCULATION FUNCTIONS ---

/**
 * Calculate card processing cost using 6-band model.
 * @param {number} volume — Monthly card turnover (GBP)
 * @param {number} avgTxn — Average transaction value (GBP)
 * @param {object} mix — Card mix percentages per band (must sum to ~100)
 * @param {object} rates — Sell rate per band (decimal, e.g. 0.004 = 0.4%)
 * @param {object} opts — { includeGateway: boolean }
 * @returns {object|null}
 */
export function calculateProcessing(volume, avgTxn, mix, rates, opts = {}) {
  if (volume <= 0 || avgTxn <= 0) return null;

  const txnCount = volume / avgTxn;
  const breakdown = {};
  let processingCost = 0;

  for (const band of CARD_BANDS) {
    const pct = (mix[band.key] || 0) / 100;
    const bandVolume = volume * pct;
    const bandCost = bandVolume * (rates[band.key] || 0);
    breakdown[band.key] = { volume: bandVolume, cost: bandCost, rate: rates[band.key] || 0, pct: pct * 100 };
    processingCost += bandCost;
  }

  const authCost = txnCount * AUTH_FEE_SELL;
  const gatewayCost = opts.includeGateway ? txnCount * GATEWAY_FEE_SELL : 0;

  return {
    processingCost,
    authCost,
    gatewayCost,
    totalProcessing: processingCost + authCost + gatewayCost,
    txnCount,
    breakdown,
  };
}

/**
 * Calculate effective rate (total cost as % of volume).
 */
export function effectiveRate(totalCost, volume) {
  if (volume <= 0) return 0;
  return (totalCost / volume) * 100;
}
